/*
 * The admin console's route manifest — the same FE-02 contract the recruiter app carries.
 *
 * Every entry here is `entitlement: null`, and the exemption is the same one each time because
 * it is the same fact: entitlements are things a *tenant* buys, and this console is not a tenant.
 * The gate still demands the declaration per route, which is right — "the whole app is exempt" is
 * exactly the sort of blanket claim that stops being true when somebody adds a screen.
 */

import type { ComponentType } from 'react';
import type { MessageKey } from '@indovation/i18n';

const PLATFORM_PLANE_EXEMPTION =
  'Platform administration console. Entitlements belong to tenants; this surface administers ' +
  'them and is not one. Authorisation here is the operator role, checked server-side on every ' +
  'request.';

export interface RouteManifestEntry {
  readonly id: string;
  readonly path: string;
  readonly entitlement: string | null;
  readonly entitlementExemption?: string;
  readonly authenticated: boolean;
  readonly nav?: { readonly labelKey: MessageKey; readonly order: number };
  /** Screens only a super admin may reach. Support sees no link and gets 403 if they guess. */
  readonly superAdminOnly?: boolean;
  readonly load: () => Promise<{ default: ComponentType }>;
}

export const ROUTES: readonly RouteManifestEntry[] = [
  {
    id: 'sign-in',
    path: '/sign-in',
    entitlement: null,
    entitlementExemption: PLATFORM_PLANE_EXEMPTION,
    authenticated: false,
    load: () => import('./screens/sign-in.js'),
  },
  {
    id: 'auth-callback',
    path: '/auth/callback',
    entitlement: null,
    entitlementExemption: PLATFORM_PLANE_EXEMPTION,
    authenticated: false,
    load: () => import('./screens/callback.js'),
  },
  {
    id: 'tenants',
    path: '/',
    entitlement: null,
    entitlementExemption: PLATFORM_PLANE_EXEMPTION,
    authenticated: true,
    nav: { labelKey: 'admin.nav.tenants', order: 0 },
    load: () => import('./screens/tenants.js'),
  },
  {
    id: 'provision',
    path: '/tenants/new',
    entitlement: null,
    entitlementExemption: PLATFORM_PLANE_EXEMPTION,
    authenticated: true,
    superAdminOnly: true,
    nav: { labelKey: 'admin.nav.newTenant', order: 1 },
    load: () => import('./screens/provision.js'),
  },
  {
    /*
     * Super admin only, and the same rule the server enforces.
     *
     * `platform_support` holds `read` across the plane, so it *could* see this screen's contents —
     * but every control on it writes, and three of the four routes behind it are refused for that
     * role. A screen whose every button 403s is worse than no link, so support gets no link.
     */
    id: 'connections',
    path: '/connections',
    entitlement: null,
    entitlementExemption: PLATFORM_PLANE_EXEMPTION,
    authenticated: true,
    superAdminOnly: true,
    nav: { labelKey: 'admin.nav.connections', order: 2 },
    load: () => import('./screens/connections.js'),
  },
  {
    /*
     * The installation's mailbox — CO8 §2.
     *
     * Super admin only, on the same reasoning as Connections beside it: every control writes, and
     * one of them stores a credential every tenant in the installation will send through. Support
     * holds `read` across the plane and would find nothing here they could do.
     *
     * A screen of its own rather than a section on Connections, and the line is worth stating: that
     * screen configures *model providers*, which is the vendor buying capacity from a vendor. This
     * one configures how the product speaks to the outside world on an agency's behalf, and the two
     * will grow apart — templates land beside this, not beside a tier binding.
     */
    id: 'mail',
    path: '/mail',
    entitlement: null,
    entitlementExemption: PLATFORM_PLANE_EXEMPTION,
    authenticated: true,
    superAdminOnly: true,
    nav: { labelKey: 'admin.nav.mail', order: 3 },
    load: () => import('./screens/mail.js'),
  },
  {
    /*
     * What the product says, as opposed to how it gets out — CO8 §3.
     *
     * Beside Mail rather than inside it, and the line between them is the one the Mail entry's own
     * comment predicted: that screen is the transport, this one is the words. They are edited by
     * different people on different days, and this is the one that will grow — every module's
     * events eventually have a message, which is a screen with two entries today and a great many
     * later.
     *
     * Super admin only, on the same reasoning as the two above: every control writes, and what it
     * writes goes out under every agency's name.
     */
    id: 'templates',
    path: '/mail/templates',
    entitlement: null,
    entitlementExemption: PLATFORM_PLANE_EXEMPTION,
    authenticated: true,
    superAdminOnly: true,
    nav: { labelKey: 'admin.nav.templates', order: 4 },
    load: () => import('./screens/templates.js'),
  },
  {
    id: 'tenant-detail',
    path: '/tenants/$tenantId',
    entitlement: null,
    entitlementExemption: PLATFORM_PLANE_EXEMPTION,
    authenticated: true,
    load: () => import('./screens/tenant-detail.js'),
  },
];
